"use client";

import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { type Control, type FieldValues, type Path, Controller } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";

interface MultiSelectCheckboxFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  endpoint: string;
  valueKey?: string;
  labelKey?: string;
  body?: Record<string, unknown>;
}

export function MultiSelectCheckboxField<T extends FieldValues>({
  control,
  name,
  label,
  endpoint,
  valueKey = "_id",
  labelKey = "title",
  body = {},
}: MultiSelectCheckboxFieldProps<T>) {
  const { data: session } = useSession();
  const baseUrl = process.env.NEXT_PUBLIC_API_CLOUDFRONT || "https://api.divineinfinitebeing.com/";

  const { data: options, isLoading } = useQuery({
    queryKey: [endpoint, body],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      return (json.results?.res ?? json.results ?? json.dt?.res ?? []) as Record<string, unknown>[];
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selectedValues: string[] = Array.isArray(field.value)
          ? (field.value as unknown[]).map(String)
          : field.value
            ? [String(field.value)]
            : [];

        function toggle(val: string) {
          if (selectedValues.includes(val)) {
            field.onChange(selectedValues.filter((v) => v !== val));
          } else {
            field.onChange([...selectedValues, val]);
          }
        }

        return (
          <div className="space-y-1.5">
            <p className="text-sm font-medium leading-none">{label}</p>
            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="size-3.5 animate-spin" /> Loading…
              </div>
            ) : (
              <div className="flex flex-wrap gap-4 pt-1">
                {(options ?? []).map((opt) => {
                  const val = String(opt[valueKey] ?? opt["_id"]);
                  const lbl = String(opt[labelKey] ?? opt["title"] ?? val);
                  return (
                    <label key={val} className="flex items-center gap-2 cursor-pointer text-sm">
                      <Checkbox
                        checked={selectedValues.includes(val)}
                        onCheckedChange={() => toggle(val)}
                        className="border-[#ffa800] data-[state=checked]:bg-[#ffa800] data-[state=checked]:border-[#ffa800]"
                      />
                      {lbl}
                    </label>
                  );
                })}
                {(options ?? []).length === 0 && (
                  <p className="text-xs text-muted-foreground">No options available</p>
                )}
              </div>
            )}
            {fieldState.error && (
              <p className="text-xs text-destructive">{fieldState.error.message}</p>
            )}
          </div>
        );
      }}
    />
  );
}
